<template>
  <section class="record-detail">
    <div class="record-hero">
      <div class="hero-toolbar">
        <PageHeader
          :eyebrow="displayName(record.student) || 'Writing record'"
          :title="record.assignment?.title || 'Writing Practice'"
          description="Submitted writing, feedback, and learning evidence in one read-only record."
          show-back
          @back="$emit('back')"
        >
          <template #actions>
            <div class="toolbar-actions">
              <button type="button" class="ui-button ui-button--primary" :disabled="!canGenerateReport" @click="downloadReport">Generate report</button>
            </div>
          </template>
        </PageHeader>
      </div>
      <div class="hero-body">
        <div class="meta-scroll" tabindex="0" aria-label="Writing record summary">
          <dl class="meta-grid">
            <div><dt>Class</dt><dd>{{ record.student?.class_group || '-' }}</dd></div>
            <div><dt>Teacher</dt><dd>{{ displayName(record.assignment?.created_by) || '-' }}</dd></div>
            <div><dt>Start</dt><dd>{{ formatDateTime(record.assignment?.start_at) }}</dd></div>
            <div><dt>Due</dt><dd>{{ formatDateTime(record.assignment?.due_at) }}</dd></div>
            <div><dt>Status</dt><dd><StatusPill :value="record.status" /></dd></div>
          </dl>
        </div>
        <div v-if="!canGenerateReport" class="ui-notice inline-message" role="status">Report is available after submission.</div>
        <div v-if="reportMessage" class="ui-notice inline-message" role="status">{{ reportMessage }}</div>
      </div>
    </div>

    <article v-if="canReview && record.experiment_participant_id" class="detail-panel">
      <ProcessHistory :key="record.experiment_participant_id" :participant-id="record.experiment_participant_id" />
    </article>
    <article class="detail-panel">
      <SectionHeader title="Assignment Prompt" description="The task brief attached to this writing record." />
      <p>{{ record.assignment?.prompt || 'No assignment prompt available.' }}</p>
    </article>

    <article class="detail-panel academic-paper" aria-label="Read-only submitted draft">
      <div class="panel-heading">
        <SectionHeader title="Draft" description="The student's submitted paper. This content is read-only." />
      </div>
      <div v-for="section in sectionEntries" :key="section[0]" class="draft-section">
        <h4>{{ titleCase(section[0]) }}</h4>
        <div v-if="section[1]" class="draft-content" v-html="renderDraftContent(section[1])"></div>
        <p v-else>No content yet.</p>
      </div>
    </article>

    <article class="detail-panel">
      <div class="panel-heading">
        <SectionHeader title="Score" description="AI draft scoring and criterion-level feedback.">
          <template v-if="canReview" #actions>
            <button type="button" class="ui-button ui-button--primary" :disabled="scoreLoading" @click="$emit('regenerate-score')">
              {{ scoreLoading ? 'Scoring...' : 'Regenerate AI score' }}
            </button>
          </template>
        </SectionHeader>
      </div>
      <p class="score-disclaimer">AI-generated scores and feedback are for reference only. The score given by the module teacher is final.</p>
      <div v-if="hasScore" class="score-content">
        <div class="score-number">{{ record.score_result.score ?? '-' }} / 4</div>
        <p v-if="record.score_result.confidence"><strong>Confidence:</strong> {{ titleCase(record.score_result.confidence) }}</p>
        <p v-if="record.score_result.summary">{{ record.score_result.summary }}</p>
        <div v-for="dimension in record.score_result.dimensions || []" :key="dimension.name" class="score-dimension">
          <strong>{{ dimension.name }}</strong>
          <span>{{ dimension.comment || 'No comment yet.' }}</span>
          <span v-for="evidence in dimension.evidence || []" :key="evidence"><strong>Evidence:</strong> {{ evidence }}</span>
        </div>
      </div>
      <div v-else-if="scoreInsufficient" class="ui-notice inline-message" role="status">
        {{ record.score_result.summary }}
      </div>
      <div v-else class="ui-empty empty-state">No AI draft score yet.</div>
      <div v-if="scoreMessage" class="ui-notice inline-message" role="status">{{ scoreMessage }}</div>
    </article>

    <article class="detail-panel">
      <div class="panel-heading">
        <SectionHeader title="Teacher Feedback" description="Teacher score and feedback for this submission.">
          <template v-if="canReview" #actions>
            <button type="button" class="ui-button ui-button--primary" :disabled="reviewSaving" @click="saveReview">
              {{ reviewSaving ? 'Saving...' : 'Save review' }}
            </button>
          </template>
        </SectionHeader>
      </div>
      <div v-if="canReview" class="teacher-review-form">
        <label>
          <span>Score</span>
          <input v-model.number="reviewForm.score" class="ui-input" type="number" min="0" max="4" step="1" />
        </label>
        <label>
          <span>Feedback</span>
          <textarea v-model="reviewForm.feedback" class="ui-textarea" rows="5" placeholder="Write teacher feedback for the student..." />
        </label>
      </div>
      <div v-else class="score-content">
        <div v-if="record.teacher_score != null" class="score-number">{{ record.teacher_score }} / 4</div>
        <p>{{ record.teacher_feedback || 'Not reviewed yet.' }}</p>
      </div>
      <div v-if="reviewMessage" class="ui-notice inline-message" role="status">{{ reviewMessage }}</div>
    </article>

    <article class="detail-panel">
      <div class="panel-heading">
        <SectionHeader title="Chat History Analysis" description="Evidence of how the student used AI during the writing process.">
          <template v-if="canReview" #actions>
            <button type="button" class="ui-button ui-button--primary" :disabled="analysisLoading" @click="$emit('run-analysis')">
              {{ analysisLoading ? 'Analyzing...' : 'Regenerate analysis' }}
            </button>
          </template>
        </SectionHeader>
      </div>
      <div v-if="analysisInsufficient" class="ui-notice inline-message" role="status">
        {{ analysisResult.summary }}
      </div>
      <div v-else-if="analysisResult" class="analysis-box">
        <p v-if="analysisResult.confidence"><strong>Confidence:</strong> {{ titleCase(analysisResult.confidence) }}</p>
        <article v-for="dimension in analysisResult.dimensions" :key="dimension.name" class="analysis-dimension">
          <div class="analysis-dimension-top">
            <strong>{{ dimension.name }}</strong>
            <span>{{ dimension.score }} / 5</span>
          </div>
          <div class="analysis-meter"><span :style="{ width: analysisMeterWidth(dimension.score) }"></span></div>
          <p>{{ dimension.comment || 'No comment yet.' }}</p>
          <p v-for="evidence in dimension.evidence || []" :key="evidence"><strong>Evidence:</strong> {{ evidence }}</p>
        </article>
        <div v-if="analysisResult.summary" class="analysis-summary">
          <strong>Overall</strong>
          <p>{{ analysisResult.summary }}</p>
        </div>
      </div>
      <div v-else class="ui-empty empty-state">No chat analysis yet.</div>
      <div v-if="analysisMessage" class="ui-notice inline-message" role="status">{{ analysisMessage }}</div>
    </article>

    <article class="detail-panel">
      <SectionHeader title="Chat History" description="The immutable conversation captured with this submission." />
      <div class="chat-summary">
        <strong>Chat History Summary</strong>
        <p>{{ chatSummary }}</p>
      </div>
      <div v-if="!record.messages?.length" class="ui-empty empty-state">No chatbot messages yet.</div>
      <div v-else class="chat-history-frame" tabindex="0" aria-label="Full chat history">
        <div v-for="message in record.messages" :key="message.id || `${message.role}-${message.created_at}-${message.content}`" :class="['drawer-message', message.role === 'user' ? 'drawer-user' : 'drawer-assistant']">
          <div v-if="message.role === 'assistant'" class="drawer-bubble assistant-output" v-html="formatAssistantMessage(message.content)"></div>
          <div v-else-if="isToolMessage(message)" class="drawer-bubble tool-user-bubble">
            <div class="tool-message-label">{{ toolMessageLabel(message) }}</div>
            <blockquote v-if="message.targetSentence">{{ message.targetSentence }}</blockquote>
            <div class="tool-message-note">{{ toolMessageNote(message) }}</div>
          </div>
          <div v-else class="drawer-bubble">{{ message.content }}</div>
        </div>
      </div>
    </article>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import PageHeader from './PageHeader.vue'
import SectionHeader from './SectionHeader.vue'
import StatusPill from './StatusPill.vue'
import ProcessHistory from '../experiments/ProcessHistory.vue'
import { buildChatSummary, saveWritingReportPdf } from '../utils/reportPdf'

const props = defineProps({
  record: { type: Object, required: true },
  mode: { type: String, default: 'student' },
  scoreLoading: { type: Boolean, default: false },
  analysisLoading: { type: Boolean, default: false },
  reviewSaving: { type: Boolean, default: false },
  scoreMessage: { type: String, default: '' },
  analysisMessage: { type: String, default: '' },
  reviewMessage: { type: String, default: '' },
})

const emit = defineEmits(['back', 'regenerate-score', 'run-analysis', 'save-review'])
const reportMessage = ref('')
const reviewForm = reactive({ score: null, feedback: '' })

const canReview = computed(() => ['teacher', 'admin'].includes(props.mode))
const isCompleted = computed(() => ['submitted', 'reviewed'].includes(props.record?.status))
const canGenerateReport = computed(() => !canReview.value || isCompleted.value)
const hasScore = computed(() => {
  const score = props.record?.score_result
  return Boolean(score && score.status !== 'insufficient_evidence' && score.score != null)
})
const scoreInsufficient = computed(() => props.record?.score_result?.status === 'insufficient_evidence')
const sectionEntries = computed(() => {
  const sections = props.record?.draft_sections || {}
  const preferred = ['background', 'objective', 'methodology', 'references']
  return [
    ...preferred.filter(key => Object.prototype.hasOwnProperty.call(sections, key)).map(key => [key, sections[key]]),
    ...Object.entries(sections).filter(([key]) => !preferred.includes(key)),
  ]
})
const analysisResult = computed(() => normaliseAnalysis(props.record?.latest_analysis))
const analysisInsufficient = computed(() => analysisResult.value?.status === 'insufficient_evidence')
const chatSummary = computed(() => buildChatSummary(props.record))

watch(
  () => props.record,
  record => {
    reviewForm.score = record?.teacher_score ?? null
    reviewForm.feedback = record?.teacher_feedback || ''
  },
  { immediate: true },
)

function saveReview() {
  emit('save-review', { score: reviewForm.score, feedback: reviewForm.feedback })
}

async function downloadReport() {
  if (!canGenerateReport.value) return
  reportMessage.value = ''
  try {
    const result = await saveWritingReportPdf(props.record, { audience: props.mode })
    reportMessage.value = result.pickedLocation
      ? 'Report saved.'
      : 'Report downloaded to your browser download folder.'
    if (!canReview.value) {
      reportMessage.value += ' Open the downloaded PDF in a PDF reader, fill in the four identification fields at the top, and save it before uploading to Moodle. These details are not sent back to this system.'
    }
  } catch (error) {
    if (error?.name === 'AbortError') {
      reportMessage.value = 'Report save canceled.'
      return
    }
    reportMessage.value = error.message || 'Could not generate report.'
  }
}

function displayName(user) {
  return user?.display_name || user?.username || ''
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('en', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

function labelStatus(status) {
  return {
    not_started: 'Not started',
    in_progress: 'In progress',
    submitted: 'Submitted',
    reviewed: 'Reviewed',
  }[status] || status
}

function statusClass(status) {
  return ['status-pill', `status-${status}`]
}

function titleCase(value = '') {
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
}

function renderDraftContent(content = '') {
  const raw = String(content)
  if (/<\/?[a-z][\s\S]*>/i.test(raw)) return raw
  return escapeHtml(raw).replace(/\n/g, '<br>')
}

function formatAssistantMessage(content = '') {
  const escaped = escapeHtml(content)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(?:^|\n)- (.+)/g, '<br><span class="assistant-bullet">$1</span>')
    .replace(/\n/g, '<br>')
  return escaped.replace(/^<br>/, '')
}

function isToolMessage(message) {
  return ['polish', 'references', 'feedback'].includes(message.mode) || Boolean(message.tool || message.targetSentence)
}

function toolMessageLabel(message) {
  return message.tool || (message.mode === 'references' ? 'find reference' : message.mode === 'polish' ? 'improve writing' : 'general feedback')
}

function toolMessageNote(message) {
  if (message.mode === 'references') return message.targetSentence ? 'Finding references for this selected text.' : 'Finding references for the full draft.'
  if (message.mode === 'polish') return 'Improving the selected writing.'
  if (message.mode === 'feedback') return 'Giving feedback on the full draft.'
  if (message.tool === 'selected chat') return message.content
  return 'Using the selected text as context.'
}

function escapeHtml(value = '') {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

const analysisDimensionNames = [
  'Tool Understanding and Operation',
  'Rhetorical Task Adaptation',
  'Critical Evaluation and Revision',
  'Ethical and Responsible Use',
  'Learning Reflection and Metacognition',
]

function analysisScore(value) {
  return Math.max(1, Math.min(5, Number(value || 1)))
}

function analysisMeterWidth(score) {
  return `${(analysisScore(score) / 5) * 100}%`
}

function normaliseAnalysis(value) {
  if (!value) return null
  let payload = value
  if (typeof value === 'string') {
    try {
      payload = JSON.parse(value)
    } catch {
      payload = { dimensions: [], summary: value }
    }
  }
  if (payload.status === 'insufficient_evidence') {
    return {
      status: 'insufficient_evidence',
      confidence: 'low',
      dimensions: [],
      summary: payload.summary || '',
    }
  }
  const byName = Object.fromEntries((payload.dimensions || []).map(item => [item.name, item]))
  const dimensions = analysisDimensionNames.map(name => ({
    name,
    score: analysisScore(byName[name]?.score),
    comment: byName[name]?.comment || '',
    evidence: byName[name]?.evidence || [],
  }))
  return {
    status: payload.status || 'ready',
    confidence: payload.confidence || 'low',
    dimensions,
    summary: payload.summary || '',
  }
}
</script>

<style scoped>
.record-detail {
  display: grid;
  max-width: 75rem;
  margin: 0 auto;
  gap: var(--space-4);
}

.record-hero,
.detail-panel {
  min-width: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  background: var(--surface);
}

.record-hero {
  display: grid;
  gap: var(--space-4);
  padding: var(--space-6);
}

.hero-toolbar,
.hero-toolbar :deep(.page-header) {
  width: 100%;
}

.hero-body {
  display: grid;
  min-width: 0;
  gap: var(--space-3);
}

.meta-scroll {
  overflow-x: auto;
  border-block: 1px solid var(--border);
  scrollbar-gutter: stable;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  min-width: 52rem;
  margin: 0;
}

.meta-grid div {
  min-width: 0;
  padding: var(--space-3) var(--space-4);
  border-left: 1px solid var(--border);
}

.meta-grid div:first-child {
  border-left: 0;
}

.meta-grid dt {
  margin-bottom: var(--space-1);
  color: var(--ink-muted);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.meta-grid dd {
  margin: 0;
  color: var(--ink);
  font-size: 0.8125rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.detail-panel {
  padding: var(--space-6);
}

.detail-panel > :deep(.section-header),
.panel-heading :deep(.section-header) {
  width: 100%;
  margin-bottom: var(--space-4);
}

.panel-heading {
  width: 100%;
}

.detail-panel > p,
.draft-content,
.score-content {
  color: var(--ink-muted);
  font-size: 0.9375rem;
  line-height: 1.7;
}

.academic-paper {
  border-top: 3px solid var(--ink);
}

.academic-paper .draft-content,
.academic-paper .draft-section > p {
  color: var(--ink);
  font-family: var(--font-academic);
  font-size: 1rem;
  line-height: 1.8;
}

.draft-section {
  border-top: 1px solid var(--border);
  padding: var(--space-4) 0;
}

.draft-section:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.draft-section h4 {
  margin: 0 0 var(--space-2);
  color: var(--accent-strong);
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.draft-content {
  white-space: pre-wrap;
}

.draft-content :deep(.reference-entry) {
  margin: 0 0 var(--space-3) var(--space-6);
  line-height: 1.6;
  text-indent: calc(-1 * var(--space-6));
  white-space: normal;
}

.score-content {
  display: grid;
  gap: var(--space-3);
}

.score-content p {
  margin: 0;
}

.score-number {
  color: var(--ink);
  font-family: var(--font-academic);
  font-size: 2rem;
}

.score-dimension {
  display: grid;
  gap: var(--space-1);
  border-top: 1px solid var(--border);
  padding-top: var(--space-3);
}

.score-dimension strong {
  color: var(--accent-strong);
  font-size: 0.8125rem;
}

.teacher-review-form {
  display: grid;
  grid-template-columns: 8.75rem minmax(0, 1fr);
  align-items: start;
  gap: var(--space-4);
}

.teacher-review-form label {
  display: grid;
  gap: var(--space-2);
  color: var(--ink-muted);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.teacher-review-form input,
.teacher-review-form textarea {
  text-transform: none;
}

.analysis-box {
  display: grid;
  gap: var(--space-3);
}

.analysis-dimension {
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-4);
  background: var(--canvas);
}

.analysis-dimension-top {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-2);
  color: var(--ink);
}

.analysis-dimension-top span {
  color: var(--accent-strong);
  font-weight: 800;
}

.analysis-meter {
  height: 0.4375rem;
  margin-bottom: var(--space-3);
  overflow: hidden;
  border-radius: 999px;
  background: var(--surface-muted);
}

.analysis-meter span {
  display: block;
  height: 100%;
  background: var(--accent);
}

.analysis-dimension p,
.analysis-summary p {
  margin: var(--space-2) 0 0;
  color: var(--ink-muted);
  line-height: 1.6;
}

.analysis-summary {
  border-top: 1px solid var(--border);
  padding-top: var(--space-4);
}

.chat-summary {
  margin-bottom: var(--space-4);
  border-left: 3px solid var(--accent);
  padding: var(--space-3) var(--space-4);
  background: var(--accent-soft);
  color: var(--ink-muted);
}

.chat-summary strong {
  color: var(--ink);
  font-size: 0.8125rem;
}

.chat-summary p {
  margin: var(--space-1) 0 0;
  line-height: 1.6;
}

.chat-history-frame {
  display: flex;
  max-height: 32.5rem;
  overflow-y: auto;
  flex-direction: column;
  gap: var(--space-2);
  border-top: 1px solid var(--border);
  padding-top: var(--space-3);
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.drawer-message {
  display: flex;
}

.drawer-user {
  justify-content: flex-end;
}

.drawer-bubble {
  max-width: 86%;
  border-radius: 0.75rem;
  padding: var(--space-3);
  background: var(--surface-muted);
  color: var(--ink);
  font-size: 0.875rem;
  line-height: 1.55;
  white-space: pre-wrap;
}

.drawer-user .drawer-bubble {
  background: var(--accent);
  color: var(--surface);
}

.drawer-user .tool-user-bubble {
  max-width: 92%;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
}

.tool-message-label {
  margin-bottom: var(--space-2);
  color: var(--accent-strong);
  font-size: 0.6875rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.tool-user-bubble blockquote {
  margin: 0;
  border-left: 3px solid var(--accent);
  padding: var(--space-2) var(--space-3);
  background: var(--surface-muted);
  color: var(--ink-muted);
  white-space: pre-wrap;
}

.tool-message-note {
  margin-top: var(--space-2);
  color: var(--ink-muted);
  font-size: 0.75rem;
}

.assistant-output {
  white-space: normal;
}

.assistant-output :deep(strong) {
  color: var(--ink);
  font-weight: 700;
}

.assistant-output :deep(.assistant-bullet) {
  display: block;
  margin-top: var(--space-1);
  padding-left: var(--space-4);
  text-indent: calc(-1 * var(--space-3));
}

.inline-message {
  font-size: 0.8125rem;
}

.empty-state {
  color: var(--ink-muted);
}

@media (max-width: 767px) {
  .record-hero,
  .detail-panel {
    padding: var(--space-4);
  }

  .teacher-review-form {
    grid-template-columns: 1fr;
  }

  .drawer-bubble {
    max-width: 94%;
  }
}
</style>
